import { useState } from "react"
import { IconChatInfoOutline18 as TooltipIcon } from "nucleo-ui-outline-18"

import { Button, Input } from "@productlane/ui"

import type {
	CustomBodyParams,
	SlashCommandCustomBodyElement,
	SlashCommandDefinition,
} from "../../types"
import { iconClassName } from "../../constants"

type TooltipCustomProps = CustomBodyParams & {
	text?: string
	tip?: string
	headline?: string
	cta?: string
	href?: string
}

const FIELDS = [
	{ key: "text", label: "Text", placeholder: "API" },
	{
		key: "tip",
		label: "Tip",
		placeholder: "Application Programming Interface",
	},
	{ key: "headline", label: "Headline", placeholder: "Optional" },
	{ key: "cta", label: "Link text", placeholder: "Optional" },
	{ key: "href", label: "Link URL", placeholder: "/docs/api" },
] as const

const TooltipBody: SlashCommandCustomBodyElement<TooltipCustomProps> = ({
	submit,
}) => {
	const [values, setValues] = useState<Record<string, string>>({})
	const ready = Boolean(values.text?.trim() && values.tip?.trim())
	const onSubmit = () => {
		if (ready) submit(values)
	}

	return (
		<div className="flex w-full flex-col gap-2">
			{FIELDS.map((field, index) => (
				<label key={field.key} className="flex flex-col gap-1">
					<span className="text-xs font-medium">{field.label}</span>
					<Input
						name={`tooltip-${field.key}`}
						type="text"
						variant="outline"
						placeholder={field.placeholder}
						value={values[field.key] ?? ""}
						onChange={(event) =>
							setValues((current) => ({
								...current,
								[field.key]: event.target.value,
							}))
						}
						onKeyDown={(event) => {
							if (event.key === "Enter") onSubmit()
						}}
						className="w-full bg-transparent text-xs outline-none"
						autoComplete="off"
						autoFocus={index === 0}
					/>
				</label>
			))}
			<Button
				onClick={onSubmit}
				variant="default"
				disabled={!ready}
				className="w-min"
			>
				Add tooltip
			</Button>
		</div>
	)
}

export const tooltipCommand: SlashCommandDefinition<TooltipCustomProps> = {
	key: "tooltip",
	title: "Tooltip",
	icon: <TooltipIcon strokeWidth={1.75} className={iconClassName} />,
	command: ({ editor, range, customBodyParams }) => {
		const text = customBodyParams?.text?.trim()
		const tip = customBodyParams?.tip?.trim()
		if (!text || !tip) {
			editor.chain().focus().deleteRange(range).run()
			return
		}
		editor
			.chain()
			.focus()
			.deleteRange(range)
			.insertTooltip(text, {
				tip,
				headline: customBodyParams.headline,
				cta: customBodyParams.cta,
				href: customBodyParams.href,
			})
			.run()
	},
	customBody: TooltipBody,
	group: "blocks",
	aliases: ["tooltip", "hint", "definition", "glossary", "hover"],
}
